import React, { useEffect, useState } from 'react'

// Constants
import { MODAL_CONSTS } from '../../const/modal-const'

// Third party
import { Link } from 'react-router-dom'

// Components
import BillDetailsFeesData from './accordionData/BillDetailsFeesData'
import BillDetailsGrandTotalData from './accordionData/BillDetailsGrandTotalData'
import BillDetailsTicketsData from './accordionData/BillDetailsTicketsData'
import BillDetailsRegistrationData from './accordionData/BillDetailsRegistrationData'
import { CreditCardPaymentMethod } from '../payment/CreditCardPaymentMethod/CreditCardPaymentMethod'

// Redux
import moment from 'moment'
import { CONST } from '../../const/const'
import { IMAGE_CONSTS } from '../../const/image-const'
import { MESSAGES_CONST } from '../../const/messages-const'
import useToasterHelper from '../../helpers/ToasterHelper'
import { IRecipientInterface } from '../../models/recipients/recipients.interface'
import { IRegistrationFeesInterface } from '../../models/registration-fees/registrationFees.interface'
import { IRegistrationTicketInterface } from '../../models/registration-tickets/registrationTicket.interface'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { ISelectedCard, IUserInterfaceExtended } from '../../models/users/user.interface'
import { UserModel } from '../../models/users/user.model'
import ExhibitorCommentAccordion from '../../pages/organizer/exhibitorProfile/accordions/ExhibitorCommentAccordion'
import ExhibitorProfileRefundAccordion from '../../pages/organizer/exhibitorProfile/accordions/ExhibitorProfileRefundAccordion'
import EventService from '../../services/eventService'
import FirestoreService from '../../services/firestoreService'
import { httpService } from '../../services/httpService'
import UserService from '../../services/firebase-service/users/userService'
import RecipientsService from '../../services/firebase-service/recipients/RecipientsService'

import {
  setBillSelectedEvent,
  setSelectedEventFees,
  setSelectedRecipient,
} from '../../store/bills/billsSlice'
import { selectedEvent } from '../../store/events/eventsSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectProfileData } from '../../store/user/userSlice'
import { FEES_CATEGORY_CONST } from '../events/views/details/EventDetailsViewComponentFees'
import ViewsLoader from '../loader/ViewsLoader'
import MainModal from './common/MainModal'
import useGetEventData from '../../hooks/users/common/useGetEventData'
import { getCurrency, getTotalPrices } from '../../helpers/payment'
import { getRegistrationEventDataInDb } from '../../helpers/registration-on-event'
import { getFloatPrice } from '../../helpers/price'

import { IOnPayload, PAYMENT_STATUS, PAYMENT_TYPE } from '../../types/payment'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'

// Constants
const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

const defaultTotal = {
  paid: 0,
  unpaid: 0,
  refund: 0,
  scratch: 0,
}

const updatedRecipient = (
  data: (IRegistrationByDayInterface | IRegistrationTicketInterface | IRegistrationFeesInterface)[],
  recipient: IRecipientInterface | null
) => {
  return data.map((cur) => ({
    ...cur,
    recipientId:
      (recipient?.recipientId === recipient?.userId ? recipient?.recipientId : cur.recipientId) ||
      null,
    recipientName:
      (recipient?.recipientId === recipient?.userId
        ? recipient?.recipientName
        : cur.recipientName) || null,
    recipientProfilePicture:
      (recipient?.recipientId === recipient?.userId
        ? recipient?.recipientProfilePicture
        : cur.recipientProfilePicture) || null,
  }))
}

type IBillTotal = {
  unpaid: number
  paid: number
  scratch: number
  refund: number
}

const BillDetailsModal = ({
  show,
  handleModal,
  dataToPassOn,
}: {
  show: boolean
  handleModal: any
  dataToPassOn: {
    eventId: string
    recipientId: string
    onSave?: () => Promise<void>
  }
}) => {
  // Hooks and vars
  const { getAllData } = useGetEventData()
  const dispatch = useAppDispatch()
  const toasterFunctions = useToasterHelper()

  const profileData = useAppSelector(selectProfileData)
  const selectedEventR = useAppSelector(selectedEvent)

  const [registrationsData, setRegistrationsData] = useState<IRegistrationByDayInterface[]>([])
  const [feesData, setFeesData] = useState<IRegistrationFeesInterface[]>([])
  const [ticketData, setTicketData] = useState<IRegistrationTicketInterface[]>([])
  const [loading, setLoading] = useState(false)
  const [receiptLoading, setReceiptLoading] = useState(false)
  const [eventLogo, setEventLogo] = useState<string>('')
  const [userProfilePicture, setUserProfilePicture] = useState<string>('')
  const [openedMenu, setOpenedMenu] = useState<string[]>([])
  const [eventLocation, setEventLocation] = useState<string>('')
  const [selectedCard, setSelectedCard] = useState<ISelectedCard>({
    type: null,
    pmId: '',
    cardNumber: '',
  })
  const [feeTotalState, setFeeTotalState] = useState<IBillTotal>(defaultTotal)
  const [governanceTotalState, setGovernanceTotalState] = useState<IBillTotal>(defaultTotal)
  const [ticketTotalState, setTicketTotalState] = useState<IBillTotal>(defaultTotal)
  const [registrationTotalState, setRegistrationTotalState] = useState<IBillTotal>(defaultTotal)
  const [taxTotalState, setTaxTotalState] = useState(0)
  const [total, setTotal] = useState<IBillTotal>(defaultTotal)
  const [modalThemeColor, setModalThemeColor] = useState<`#${string}`>('#FFD96440')
  const [userData, setUserData] = useState<IUserInterfaceExtended | null>(null)
  const [organiserData, setOrganiserData] = useState<IUserInterfaceExtended | null>(null)
  const [recipient, setRecipient] = useState<IRecipientInterface | null>(null)
  const [eventReviewPublish, setEventReviewPublish] = useState<IEventReviewPublish | null>(null)

  const { eventId, recipientId } = dataToPassOn

  useEffect(() => {
    const getEventDetails = async () => {
      try {
        setReceiptLoading(true)
        const recipientsInDb_ = await RecipientsService.getByRecipientsByEvent(eventId, recipientId)
        const recipient_ = recipientsInDb_.find((item) => item.recipientId === recipientId)
        if (!recipient_) {
          toasterFunctions.error({
            message: 'Recipient not found',
          })
          return
        }

        const eventService = new EventService(recipient_?.eventId || '')
        const eventLocation = await eventService.getEventLocation
        const eventOwner = await eventService.getPublishedEvent

        const ownerSnapshot = await FirestoreService.getItem(
          COLLECTIONS.USERS.NAME,
          eventOwner?.owner ?? ''
        )
        const owner = UserModel.fromFirestoreDoc(ownerSnapshot).toObject()

        const userSnapshot = await UserService.getUserInfoById(recipient_.recipientId)
        const user = UserModel.fromFirestoreDoc(userSnapshot).toObject()

        let eventLogo_ = owner?.userProfilePicture
          ? owner.userProfilePicture
          : '/assets/placeholders/Emptyuser.png'

        const userProfilePicture_ = user.userProfilePicture
          ? user.userProfilePicture
          : '/assets/placeholders/Emptyuser.png'

        setEventLogo(eventLogo_)
        setUserProfilePicture(userProfilePicture_)
        setEventLocation(eventLocation.combinedLocation)
        setUserData(user)
        setOrganiserData(owner)
        setRecipient(recipient_ || null)
        setEventReviewPublish(eventOwner)

        const { registrationsByDay, registrationsFees, registrationsTickets } =
          await getRegistrationEventDataInDb({
            registrationId: recipient_.registrationDocId || '',
            type: ['classes', 'fees', 'tickets'],
          })
        const filteredRegistrationsByDay = registrationsByDay.filter((reg) => !reg.isDivision)

        setRegistrationsData(filteredRegistrationsByDay)
        setFeesData(registrationsFees)
        setTicketData(registrationsTickets)

        const {
          totalRegistrations,
          totalFees,
          totalGovernance,
          totalTickets,
          totalPaidTax,
          unpaidTotal,
          paidTotal,
          scratchTotal,
          refundTotal,
        } = getTotalPrices({
          registrationsEntries: filteredRegistrationsByDay,
          registrationsFees: registrationsFees,
          registrationsTickets: registrationsTickets,
          isChargeSalesTax: eventOwner?.EventPaymentSettings?.chargeSalesTax ?? false,
        })

        setFeeTotalState(totalFees)
        setGovernanceTotalState(totalGovernance)
        setTicketTotalState(totalTickets)
        setRegistrationTotalState(totalRegistrations)
        setTaxTotalState(totalPaidTax)
        setTotal({
          unpaid: unpaidTotal,
          paid: paidTotal,
          scratch: scratchTotal,
          refund: refundTotal,
        })
      } catch (error) {
        console.error(error, 'error')
        toasterFunctions.error({
          message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
        })
      } finally {
        setReceiptLoading(false)
      }
    }

    if (recipientId) getEventDetails().then()
  }, [eventId, recipientId, toasterFunctions])

  useEffect(() => {
    if (recipient?.refundStatus === 'pending') {
      setModalThemeColor('#FFD96440')
    } else {
      setModalThemeColor('#00B6AA26')
    }
  }, [recipient])

  useEffect(() => {
    if (!!recipient?.eventId) {
      getAllData(recipient.eventId, ['v01_event_payments_settings']).then()
    }
  }, [recipient, getAllData])

  useEffect(() => {
    const { Event, EventFees } = selectedEventR

    if (!Event || !EventFees) return

    dispatch(setBillSelectedEvent(Event))
    dispatch(setSelectedEventFees(EventFees))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventR.Event, selectedEventR.EventFees])

  useEffect(() => {
    if (recipient?.id) {
      dispatch(setSelectedRecipient(recipient))
    }
  }, [recipient, dispatch])

  // Functions
  const closeModal = () => {
    handleModal(false, MODAL_CONSTS.BILL_BRIEFE)
  }

  const handleToggle = (value?: any) => {
    const exist = openedMenu.includes(value)

    if (exist) {
      setOpenedMenu(() => {
        return openedMenu.filter((current: any) => {
          return current !== value
        })
      })
    } else {
      setOpenedMenu((prevState: any) => [...prevState, value])
    }
  }

  const onMarkAsPaid = async () => {
    handleModal(false, MODAL_CONSTS.BILL_BRIEFE)
    handleModal(true, MODAL_CONSTS.ORGANIZER_MARK_PAID, {
      bill: recipient,
      registrationsByDay: registrationsData,
      registrationsFees: feesData,
      registrationsTickets: ticketData,
      selectedEvent: selectedEventR,
    })
  }

  const onPayAmount = async () => {
    setLoading(true)

    try {
      if (!selectedCard?.pmId) {
        return toasterFunctions.info({ message: MESSAGES_CONST.PLEASE_SELECT_CARD })
      }

      const tickets = updatedRecipient(ticketData, recipient) as IRegistrationTicketInterface[]
      const fees = updatedRecipient(feesData, recipient) as IRegistrationFeesInterface[]
      const classes = updatedRecipient(
        registrationsData,
        recipient
      ) as IRegistrationByDayInterface[]

      if (recipient?.eventId && recipient.registrationDocId) {
        const payData: IOnPayload = {
          paymentType: PAYMENT_TYPE.CARD,
          paymentStatus: PAYMENT_STATUS.PAID,
          amountToPay: total.unpaid / 100,
          pmId: selectedCard?.pmId || null,
          cardNumber: selectedCard?.cardNumber || null,
          description: 'Event registration',
          type: 'pay_registration_bill',
          registrationId: recipient?.registrationDocId ?? null,
          registrationsByDay: classes.filter((entry) => !entry.paymentStatus && !entry.isScratched),
          tickets: tickets.filter((ticket) => !ticket.paymentStatus && !ticket.isScratched),
          fees: fees.filter((fee) => !fee.paymentStatus && !fee.isScratched),
          ridersTeamMembers: [],
          payerId: recipient?.recipientId ?? null,
          registrarId: recipient?.userId ?? null,
          eventId: recipient.eventId,
          isTicketPayment: false,
        }

        await httpService({
          url: 'on_pay',
          method: 'POST',
          data: payData,
        })

        toasterFunctions.success({ message: MESSAGES_CONST.PAYMENT_DONE })
        if (dataToPassOn.onSave) await dataToPassOn.onSave()
        handleModal(false, MODAL_CONSTS.BILL_BRIEFE)
      } else {
        return toasterFunctions.info({
          message: '"EventId" or "recipientId" not found',
        })
      }
    } catch (error: any) {
      console.error(error, 'error')
      toasterFunctions.error({
        message: error?.response.data?.response || MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      setLoading(false)
    }
  }

  const openAddNewCardModal = () => {
    handleModal(false, MODAL_CONSTS.BILL_BRIEFE)
    handleModal(true, MODAL_CONSTS.CARD, {
      add_card: true,
      re_open_modal: true,
      modal_name: MODAL_CONSTS.BILL_BRIEFE,
      ...dataToPassOn,
      organizerId: organiserData?.id,
      user: userData,
    })
  }

  const currency = getCurrency(selectedEventR?.EventPaymentSettings?.currency)

  let allowPay = true
  if (selectedCard.type === 'card') {
    allowPay =
      eventReviewPublish?.EventPaymentSettings?.allowCreditCardPayment ||
      eventReviewPublish?.EventPaymentSettings?.allowACHPayment ||
      organiserData?.id === profileData.id
  } else if (selectedCard.type === 'us_bank_account') {
    allowPay =
      eventReviewPublish?.EventPaymentSettings?.allowACHPayment ||
      organiserData?.id === profileData.id
  }

  return (
    <MainModal
      show={show}
      title="Receipt"
      type="BILL_BRIEFE"
      titleClassName="text-base pl-2 pb-2"
      className="relative"
      setHeightAsPerContent={true}
      size={'4xl'}
      buttons={[
        ...(total.unpaid > 0
          ? [
              {
                loading,
                label: 'Pay ' + getFloatPrice(`${currency}${total.unpaid / 100}`),
                fullWidth: true,
                disabled: loading || !allowPay,
                onClick: onPayAmount,
                bgClass: '!bg-SeabiscuitMainThemeColor',
                textClass: '!text-SeabiscuitWhiteThemeColor',
              },
              {
                label: 'Close',
                fullWidth: true,
                disabled: loading,
                bgClass: '!bg-SeabiscuitLightThemeColor',
                borderClass: 'border border-transparent',
                textClass: '!text-SeabiscuitLightTextColor',
                onClick: closeModal,
              },
            ]
          : []),
      ]}
    >
      {receiptLoading ? (
        <div className="w-full h-full bg-white z-10 flex justify-center items-center">
          <ViewsLoader className="flex items-center w-full justify-center" size="xl" color="red" />
        </div>
      ) : (
        <div className="text-SeabiscuitDark200ThemeColor">
          {recipient?.id ? (
            <div id="receipt">
              <div className="flex flex-col md:flex-row md:justify-between pb-2">
                <div>
                  <div className="flex items-center my-3">
                    <img
                      className="rounded-md mr-4 h-9 w-9 object-cover"
                      src={eventLogo ? eventLogo : IMAGE_CONSTS.PLACEHOLDERS.EVENT}
                      alt="eventImage"
                    />

                    <p className="text-md font-semibold text-SeabiscuitDark200ThemeColor">
                      {' '}
                      <div className="opacity-50 text-sm font-normal">Event</div>
                      {recipient?.eventName}
                    </p>
                  </div>
                  <div className="flex items-center my-3">
                    <img
                      className="rounded-md mr-4 h-9 w-9 object-cover"
                      src={userProfilePicture}
                      alt="userIcon"
                    />
                    <p className="text-md text-SeabiscuitDark200ThemeColor capitalize gap-1">
                      <div className="opacity-50 text-xs">From </div> {recipient?.userName}
                    </p>
                  </div>
                  <div className="flex items-center my-3">
                    <span className="mr-2 flex items-center">
                      <img
                        className="bg-[#fef5f6] rounded-lg p-[0.4rem]"
                        src="/assets/cp_icons/Location-2.svg"
                        alt="locationIcon"
                      />
                    </span>
                    <p className="text-md text-SeabiscuitDark200ThemeColor ml-2 gap-1">
                      <div className="opacity-50 text-xs">
                        {moment(recipient?.created).format('MMM Do, YYYY')}{' '}
                      </div>{' '}
                      {eventLocation}
                    </p>
                  </div>
                </div>

                <div className="mb-4 flex md:justify-end">
                  <div className="flex-col md:justify-end">
                    {total.unpaid > 0 && (
                      <p className="text-lg text-SeabiscuitMainThemeColor font-bold rounded-md flex md:justify-end">
                        {getFloatPrice(`${currency}${total.unpaid / 100}`)} owing
                      </p>
                    )}
                    <div className="flex my-1 mb-3 md:justify-end flex-wrap gap-1">
                      <p className="text-SeabiscuitDark200ThemeColor">
                        {getFloatPrice(`${currency}${total.paid / 100}`)} paid,
                      </p>
                      <p className="text-SeabiscuitDark200ThemeColor">
                        {getFloatPrice(`${currency}${total.scratch / 100}`)} scratched,
                      </p>
                      <p className="text-SeabiscuitDark200ThemeColor">
                        {getFloatPrice(`${currency}${total.refund / 100}`)} refunded
                      </p>
                    </div>
                    <div className="-ml-2 md:ml-0 md:-mr-2 flex flex-wrap divide-x divide-solid divide-SeabiscuitDark200ThemeColor/50 leading-none gap-y-2 md:justify-end">
                      {!receiptLoading ? (
                        <Link
                          to={`${CONST.ROUTES.RECEIPT.URL}/${recipient.registrationDocId}`}
                          target="_blank"
                          className="text-SeabiscuitDark200ThemeColor opacity-50 hover:opacity-100 underline text-sm px-2"
                        >
                          View Receipt
                        </Link>
                      ) : null}
                      {profileData?.userType === CONST.USE_MODE.ORGANIZER && total.unpaid > 0 && (
                        <button
                          type="button"
                          onClick={onMarkAsPaid}
                          className="text-SeabiscuitDark200ThemeColor opacity-50 hover:opacity-100 underline text-sm px-2"
                        >
                          Mark As Paid
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="h-[1px] bg-[#D3DAEE] my-2 mb-6"></div>

              {/* Registration accordion */}
              {
                <div
                  className={`mt-2 border-[#D3DAEE] border p-4 rounded-lg ${openedMenu.includes('1') ? 'activeAccordion' : ''}`}
                >
                  <div
                    className={`text-SeabiscuitDark200ThemeColor flex justify-between items-center ${openedMenu.includes('1') ? 'font-semibold' : 'font-normal'}`}
                  >
                    Classes
                    {!receiptLoading ? (
                      <div className="flex flex-col md:flex-row md:items-center">
                        <div
                          className="status text-SeabiscuitGreenThemeColor rounded-full p pl-4"
                          onClick={() => handleToggle('1')}
                        >
                          {openedMenu.includes('1') ? (
                            <div className={'add_icon cursor-pointer'}>
                              <img src={'/assets/og_icons/CloseArrow.svg'} alt="Close" />
                            </div>
                          ) : (
                            <div className={'remove_icon cursor-pointer'}>
                              <img src={'/assets/og_icons/OpenArrow.svg'} alt="Open" />
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className={`faqs-content ${openedMenu.includes('1') ? 'activeTab' : ''}`}>
                    <div className="faqs-content-inside mt-4">
                      <BillDetailsRegistrationData
                        modalThemeColor={modalThemeColor}
                        registrationsByDay={registrationsData ?? []}
                        refundregistrationTotalState={registrationTotalState.refund / 100}
                        showRefund={recipient.amountScratched > 0}
                        currency={currency}
                      />
                    </div>
                  </div>
                </div>
              }

              {/* Fees accordion */}
              <div
                className={`mt-2 border-[#D3DAEE] border p-4 rounded-lg ${openedMenu.includes('2') ? 'activeAccordion' : ''}`}
              >
                <div
                  className={`text-SeabiscuitDark200ThemeColor flex justify-between items-center ${openedMenu.includes('2') ? 'font-semibold' : 'font-normal'}`}
                >
                  Fees
                  <div className="flex items-center">
                    <div
                      className="status text-SeabiscuitGreenThemeColor rounded-full p pl-4"
                      onClick={() => handleToggle('2')}
                    >
                      {openedMenu.includes('2') ? (
                        <div className={'add_icon cursor-pointer'}>
                          <img src={'/assets/og_icons/CloseArrow.svg'} alt="Close" />
                        </div>
                      ) : (
                        <div className={'remove_icon cursor-pointer'}>
                          <img src={'/assets/og_icons/OpenArrow.svg'} alt="Open" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`faqs-content ${openedMenu.includes('2') ? 'activeTab' : ''}`}>
                  <div className="faqs-content-inside mt-4">
                    <BillDetailsFeesData
                      data={
                        feesData.length
                          ? feesData.filter(
                              (data) => data.feesItemCategory !== FEES_CATEGORY_CONST.GOVERNANCE
                            )
                          : []
                      }
                      modalThemeColor={modalThemeColor}
                      showRefund={recipient.amountScratched > 0}
                      refundFeeTotalState={feeTotalState.refund / 100}
                      currency={currency}
                    />
                  </div>
                </div>
              </div>
              {/* Mandatory accordion */}
              <div
                className={`mt-2 border-[#D3DAEE] border p-4 rounded-lg ${openedMenu.includes('5') ? 'activeAccordion' : ''}`}
              >
                <div
                  className={`text-SeabiscuitDark200ThemeColor flex justify-between items-center ${openedMenu.includes('5') ? 'font-semibold' : 'font-normal'}`}
                >
                  Mandatory Fees
                  <div className="flex items-center">
                    <div
                      className="status text-SeabiscuitGreenThemeColor rounded-full p pl-4"
                      onClick={() => handleToggle('5')}
                    >
                      {openedMenu.includes('5') ? (
                        <div className={'add_icon cursor-pointer'}>
                          <img src={'/assets/og_icons/CloseArrow.svg'} alt="Close" />
                        </div>
                      ) : (
                        <div className={'remove_icon cursor-pointer'}>
                          <img src={'/assets/og_icons/OpenArrow.svg'} alt="Open" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`faqs-content ${openedMenu.includes('5') ? 'activeTab' : ''}`}>
                  <div className="faqs-content-inside mt-4">
                    <BillDetailsFeesData
                      modalThemeColor={modalThemeColor}
                      data={
                        feesData.length
                          ? feesData.filter(
                              (data) => data.feesItemCategory === FEES_CATEGORY_CONST.GOVERNANCE
                            )
                          : []
                      }
                      showRefund={recipient.amountScratched > 0}
                      refundFeeTotalState={governanceTotalState.refund / 100}
                      currency={currency}
                    />
                  </div>
                </div>
              </div>

              {/* Tickets accordion */}
              <div
                className={`mt-2 border-[#D3DAEE] border p-4 rounded-lg ${openedMenu.includes('3') ? 'activeAccordion' : ''}`}
              >
                <div
                  className={`text-SeabiscuitDark200ThemeColor flex justify-between items-center ${openedMenu.includes('3') ? 'font-semibold' : 'font-normal'}`}
                >
                  Tickets
                  <div className="flex items-center">
                    <div
                      className="status text-SeabiscuitGreenThemeColor rounded-full p pl-4"
                      onClick={() => handleToggle('3')}
                    >
                      {openedMenu.includes('3') ? (
                        <div className={'add_icon cursor-pointer'}>
                          <img src={'/assets/og_icons/CloseArrow.svg'} alt="Close" />
                        </div>
                      ) : (
                        <div className={'remove_icon cursor-pointer'}>
                          <img src={'/assets/og_icons/OpenArrow.svg'} alt="Open" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`faqs-content ${openedMenu.includes('3') ? 'activeTab' : ''}`}>
                  <div className="faqs-content-inside mt-4">
                    <BillDetailsTicketsData
                      data={ticketData}
                      modalThemeColor={modalThemeColor}
                      showRefund={recipient.amountScratched > 0}
                      refundgovernanceTotalState={ticketTotalState.refund / 100}
                      currency={currency}
                    />
                  </div>
                </div>
              </div>

              {/* Grand Total accordion */}
              <div
                className={`mt-2 border-[#D3DAEE] border p-4 rounded-lg ${openedMenu.includes('4') ? 'activeAccordion' : ''}`}
              >
                <div
                  className={`text-SeabiscuitDark200ThemeColor flex justify-between items-center ${openedMenu.includes('4') ? 'font-semibold' : 'font-normal'}`}
                >
                  Grand Total
                  <div className="flex items-center">
                    <div
                      className="status text-SeabiscuitGreenThemeColor rounded-full p pl-4"
                      onClick={() => handleToggle('4')}
                    >
                      {openedMenu.includes('4') ? (
                        <div className={'add_icon cursor-pointer'}>
                          <img src={'/assets/og_icons/CloseArrow.svg'} alt="Close" />
                        </div>
                      ) : (
                        <div className={'remove_icon cursor-pointer'}>
                          <img src={'/assets/og_icons/OpenArrow.svg'} alt="Open" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`faqs-content ${openedMenu.includes('4') ? 'activeTab' : ''}`}>
                  <div className="faqs-content-inside mt-4">
                    <BillDetailsGrandTotalData
                      modalThemeColor={modalThemeColor}
                      totalPayments={{
                        tax: getFloatPrice(`${currency}${taxTotalState / 100}`),
                        fees: getFloatPrice(`${currency}${feeTotalState.paid / 100}`),
                        governance: getFloatPrice(`${currency}${governanceTotalState.paid / 100}`),
                        tickets: getFloatPrice(`${currency}${ticketTotalState.paid / 100}`),
                        registrations: getFloatPrice(
                          `${currency}${registrationTotalState.paid / 100}`
                        ),
                      }}
                      grandTotal={getFloatPrice(`${currency}${total.paid / 100}`)}
                    />
                  </div>
                </div>
              </div>
              {total.paid > 0 && recipient.modified && (
                <div className="mt-2 border border-[#D3DAEE] py-3.5 rounded-lg p-2 flex items-center mb-2">
                  <img src="/assets/cp_icons/Calendar-1.svg" className="w-6 h-6 mr-4" alt="card" />
                  <span className="text-SeabiscuitDark200ThemeColor">
                    Last Paid: {moment(recipient.modified).format('MMM DD, YYYY')}
                  </span>
                </div>
              )}
              {recipient.amountRefunded > 0 && (
                <div className="mt-2 border-[#D3DAEE] border p-4 rounded-lg">
                  <ExhibitorProfileRefundAccordion
                    total={total.paid / 100}
                    refundTotal={total.refund / 100}
                    currency={currency}
                  />
                </div>
              )}

              {recipient.comments.find((data) => data.comment) && (
                <div className="mt-2">
                  <ExhibitorCommentAccordion
                    hide={false}
                    commenttext={recipient.comments.find((data) => data.comment)}
                  />
                </div>
              )}
              {total.unpaid > 0 && (
                <div className="mt-3 w-full border-solid p-4 border-[#D3DAEE] border rounded-2xl">
                  <p className="mb-4 text-sm text-black">Select card to pay</p>
                  <CreditCardPaymentMethod
                    availablePaymentMethods={['cards']}
                    selectedPaymentMethod="credit-card"
                    selectedCard={selectedCard}
                    setSelectedCard={setSelectedCard}
                    user={userData}
                    organizer={organiserData}
                    openAddNewCardModal={openAddNewCardModal}
                  />
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </MainModal>
  )
}

export default BillDetailsModal
